import React, { useState, useRef, useEffect, useCallback } from 'react';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify } from '../../context/FeedbackContext';
import './ProductForm.css';
import './ConfigurableProductForm.css';
import { FaRegTrashAlt } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';

const defaultAttributes = () => ([{
  id: Date.now(),
  attributeReference: null,
  name: 'Size',
  options: [
    { id: Date.now() + 1, optionReference: null, label: 'Large', priceAdj: '3' },
    { id: Date.now() + 2, optionReference: null, label: 'Medium', priceAdj: '2' },
    { id: Date.now() + 3, optionReference: null, label: 'Small', priceAdj: '0' },
  ],
}]);

function ConfigurableProductEditForm({ onNavigate, product }) {
  const p = product || {};
  const { selectedRestaurant } = useRestaurant() || {};
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    tenantCode: '',
    itemName: p.itemName || '',
    itemDesc: p.itemDesc || '',
    catReference: p.catReference || '',
    basePrice: p.basePrice !== null ? parseFloat(p.basePrice).toFixed(2) : '0.00',
    memberPrice: p.memberPrice !== null ? parseFloat(p.memberPrice).toFixed(2) : '0.00',
    festivalPrice: p.festivalPrice !== null ? parseFloat(p.festivalPrice).toFixed(2) : '0.00',
    seasonal: p.seasonal === 1 ? 'seasonal' : 'regular',
    status: p.status === 1 ? 'Published' : 'Unpublished',
    printerName: p.printerName || '',
    printerReference: p.printerReference || '',
  });
  const [imageFile, setImageFile] = useState(null);

  

   const [imagePreview, setImagePreview] = useState(
    p.itemPic && p.itemPic !== 'undefined' && p.itemPic !== '' ? p.itemPic : ''
  );
  const [attributes, setAttributes] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageInputRef = useRef(null);

  // 打印机列表
  const [prinList, setPrinList] = useState([]);

  const DESC_MAX = 50;

  const fetchProductDetail = useCallback(async () => {
    if (!p.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/product/detail?reference=${p.reference}`), { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200 && data.data?.attributes?.length > 0) {
        setAttributes(data.data.attributes.map(attr => ({
          id: Date.now() + Math.random(),
          attributeReference: attr.itemAttributeReference || null,
          name: attr.attName || '',
          options: (attr.options || []).map(opt => ({
            id: Date.now() + Math.random(),
            optionReference: opt.itemOptionReference || null,
            label: opt.option || '',
            priceAdj: opt.priceAdjustment !== null ? String(opt.priceAdjustment) : '0',
          })),
        })));
      } else {
        setAttributes(defaultAttributes());
      }
    } catch (err) {
      console.error('Failed to fetch product detail', err);
      setAttributes(defaultAttributes());
    }
  }, [p.reference]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchProductDetail();
  }, [fetchProductDetail]);

  const fetchCategories = useCallback(async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/catalog/categories/options?tenantReference=${selectedRestaurant.reference}`), { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        setCategories(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  }, [selectedRestaurant]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (selectedRestaurant) {
      setForm(prev => ({ ...prev, tenantCode: selectedRestaurant.code }));
    }
  }, [selectedRestaurant]);

  // 查询打印机
  const getPrintIn = async (tenantRef) => {
    if (!tenantRef) return;
    try {
      const res = await fetch(
        buildApiUrl(`/setting/printers/by-tenant?tenantReference=${tenantRef}`),
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.code === 200) {
        setPrinList(data.data);
      }
    } catch (err) {
      notify(err, 'error');
      console.error("Failed to fetch printers", err);
    }
  };

  useEffect(() => {
    getPrintIn(selectedRestaurant?.reference);
  }, [selectedRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  // 打印机选择
  const handleFormChangePrint = (e) => {
    const selectedReference = e.target.value;
    const selectedPrinter = prinList.find(pr => pr.reference === selectedReference);
    setForm(prev => ({
      ...prev,
      printerReference: selectedReference,
      printerName: selectedPrinter?.printerName || '',
    }));
    setFormErrors(prev => ({ ...prev, printerReference: undefined }));
  };

  // 初始加载标记，区分 API 回显和用户手动切换分类
  const initRef = useRef(false);
  useEffect(() => {
    if (p.printerReference) {
      initRef.current = true;
      return;
    }
    if (!initRef.current) {
      if (form.catReference && prinList.length > 0) {
        const selectedCat = categories.find(c => c.reference === form.catReference);
        if (selectedCat?.printerReference) {
          const matchedPrinter = prinList.find(pr => pr.reference === selectedCat.printerReference);
          if (matchedPrinter) {
            setForm(prev => ({ ...prev, printerReference: matchedPrinter.reference, printerName: matchedPrinter.printerName }));
          }
        }
      }
      initRef.current = true;
      return;
    }
    // 用户手动切换分类：按分类自动关联打印机
    const selectedCat = categories.find(c => c.reference === form.catReference);
    if (selectedCat?.printerReference) {
      const matchedPrinter = prinList.find(pr => pr.reference === selectedCat.printerReference);
      if (matchedPrinter) {
        setForm(prev => ({ ...prev, printerReference: matchedPrinter.reference, printerName: matchedPrinter.printerName }));
      }
    } else {
      setForm(prev => ({ ...prev, printerReference: '', printerName: '' }));
    }
  }, [form.catReference, categories, prinList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (categories.length > 0 && !form.catReference) {
      setForm(prev => ({ ...prev, catReference: categories[0].reference }));
    }
  }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const errors = {};
    if (!form.itemName.trim()) errors.itemName = 'Product name is required.';
    if (!form.itemDesc.trim()) errors.itemDesc = 'Product description is required.';
    else if (form.itemDesc.trim().length > DESC_MAX) errors.itemDesc = `Description cannot exceed ${DESC_MAX} characters.`;
    if (!form.printerReference) errors.printerReference = 'Please select a printer.';
    const seen = new Set();
    for (const attr of attributes) {
      const key = attr.name.trim().toLowerCase();
      if (seen.has(key)) {
        errors.attributes = `Duplicate attribute name: "${attr.name.trim()}". Each attribute must have a unique name.`;
        break;
      }
      seen.add(key);
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const addAttribute = () => {
    setAttributes(prev => [...prev, {
      id: Date.now(),
      attributeReference: null,
      name: '',
      options: [{ id: Date.now() + 1, optionReference: null, label: '', priceAdj: '0' }],
    }]);
  };

  const removeAttribute = (attrId) => {
    setAttributes(prev => prev.filter(a => a.id !== attrId));
  };

  const updateAttributeName = (attrId, name) => {
    setAttributes(prev => prev.map(a => a.id === attrId ? { ...a, name } : a));
  };

  const addOption = (attrId) => {
    setAttributes(prev => prev.map(a =>
      a.id === attrId
        ? { ...a, options: [...a.options, { id: Date.now(), optionReference: null, label: '', priceAdj: '0' }] }
        : a
    ));
  };

  const updateOption = (attrId, optId, field, value) => {
    setAttributes(prev => prev.map(a =>
      a.id === attrId
        ? { ...a, options: a.options.map(o => o.id === optId ? { ...o, [field]: value } : o) }
        : a
    ));
  };

  const removeOption = (attrId, optId) => {
    setAttributes(prev => prev.map(a =>
      a.id === attrId ? { ...a, options: a.options.filter(o => o.id !== optId) } : a
    ));
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        reference: p.reference,
        tenantReference: selectedRestaurant?.reference,
        tenantCode: form.tenantCode,
        catReference: form.catReference,
        itemName: form.itemName.trim(),
        itemDesc: form.itemDesc.trim(),
        basePrice: parseFloat(form.basePrice) || 0,
        memberPrice: parseFloat(form.memberPrice) || 0,
        festivalPrice: parseFloat(form.festivalPrice) || 0,
        seasonal: form.seasonal === 'seasonal' ? 1 : 0,
        status: form.status === 'Published' ? 1 : 0,
        printerReference: form.printerReference || null,
        attributes: attributes.map((a, ai) => ({
          itemAttributeReference: a.attributeReference || null,
          attName: a.name,
          position: ai + 1,
          options: a.options.map((o, oi) => ({
            itemOptionReference: o.optionReference || null,
            option: o.label,
            priceAdjustment: parseFloat(o.priceAdj) || 0,
            position: oi + 1,
          })),
        })),
      };

      const formData = new FormData();
      formData.append('configurableProduct', JSON.stringify(payload));
      if (imageFile) formData.append('image', imageFile);

      const res = await fetch(buildApiUrl('/product/configurable/edit'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (data.code === 200) {
        notify('Product updated successfully!', 'success');
        onNavigate('Product');
      } else {
        notify('Failed to update product: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Error updating product:', err);
      notify('Failed to update product: ' + err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onNavigate('Product');
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="pf-wrapper">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item =>
            (
              <li key={item} className={item === 'Product' ? 'active' : ''} onClick={() => onNavigate(getRouteFromItem(item))}>
                <span className="nav-icon">{navIcons[item]}</span> {item}
              </li>
            )
          )}
        </ul>
      </nav>

      <main className="pf-main">
        <h1 className="pf-title">Edit Configurable Product</h1>

        <div className="pf-card">
          {/* Row: Product Name + Categories */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Product Name <span className="pf-required">*</span></label>
              <input
                className={`pf-input${formErrors.itemName ? ' pf-input-error' : ''}`}
                name="itemName"
                value={form.itemName}
                onChange={handleChange}
                placeholder="Enter product name"
              />
              {formErrors.itemName
                ? <span className="pf-field-error">{formErrors.itemName}</span>
                : <span className="pf-hint">A product name is required and recommended to be unique</span>
              }
            </div>
            <div className="pf-field">
              <label>Categories</label>
              <select className="pf-select" name="catReference" value={form.catReference} onChange={handleChange} disabled={categories.length === 0}>
                {categories.length === 0
                  ? <option value="" disabled style={{ color: '#aaa' }}>-- no categories added. --</option>
                  : categories.map(c => (
                      <option key={c.reference} value={c.reference}>{c.catName}</option>
                    ))}
              </select>
            </div>
          </div>

          {/* Product Description */}
          <div className="pf-field">
            <label>Product Description <span className="pf-required">*</span></label>
            <div className="pf-editor">
              <textarea
                className={`pf-textarea${formErrors.itemDesc ? ' pf-input-error' : ''}`}
                name="itemDesc"
                value={form.itemDesc}
                onChange={handleChange}
                placeholder="Provide a detailed description of your product"
                maxLength={DESC_MAX + 1}
              />
            </div>
            {formErrors.itemDesc && <span className="pf-field-error">{formErrors.itemDesc}</span>}
          </div>

          {/* Image */}
          <div className="pf-field">
            {imagePreview ? (
              <div className="pf-img-edit-wrap">
                <img src={imagePreview} alt="Product" className="pf-img-thumb" />
                <button type="button" className="pf-img-edit-icon" onClick={() => imageInputRef.current.click()}>
                  <FiEdit2 />
                </button>
              </div>
            ) : (
              <button type="button" className="pf-upload-btn" onClick={() => imageInputRef.current.click()}>
                Upload Product Images
              </button>
            )}
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
          </div>

          {/* Row: Base Price + Seasonal */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Base Price</label>
              <input className="pf-input" name="basePrice" type="number" step="0.01" min="0" value={form.basePrice} onChange={handleChange} />
            </div>
            <div className="pf-field">
              <label>Seasonal</label>
              <select className="pf-select" name="seasonal" value={form.seasonal} onChange={handleChange}>
                <option value="regular">regular</option>
                <option value="seasonal">seasonal</option>
              </select>
            </div>
          </div>

          {/* Membership Price + Festival Price */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Membership Price</label>
              <input className="pf-input" name="memberPrice" type="number" step="0.01" min="0" value={form.memberPrice} onChange={handleChange} />
            </div>
            <div className="pf-field">
              <label>Festival Price</label>
              <input className="pf-input" name="festivalPrice" type="number" step="0.01" min="0" value={form.festivalPrice} onChange={handleChange} />
            </div>
          </div>

          {/* Attributes */}
          {formErrors.attributes && <span className="pf-field-error">{formErrors.attributes}</span>}
          {attributes.map(attr => (
            <div key={attr.id} className="cpf-variant-block">
              <div className="cpf-variant-header">
                <span className="cpf-variant-title">Individual Variant Rules</span>
                <div className="cpf-header-actions">
                  <button type="button" className="cpf-add-attr-btn" onClick={addAttribute}>+ Add attributes</button>
                  {attributes.length > 1 && (
                    <button type="button" className="cpf-delete-attr-btn" onClick={() => removeAttribute(attr.id)}><FaRegTrashAlt /></button>
                  )}
                </div>
              </div>
              <div className="cpf-variant-body">
                <div className="cpf-attr-name-row">
                  <span className="cpf-attr-name-label">Attribute Name:</span>
                  <input
                    className="cpf-attr-name-input"
                    value={attr.name}
                    onChange={e => updateAttributeName(attr.id, e.target.value)}
                  />
                </div>
                {attr.options.map(opt => (
                  <div key={opt.id} className="cpf-option-row">
                    <input
                      className="cpf-option-label"
                      value={opt.label}
                      onChange={e => updateOption(attr.id, opt.id, 'label', e.target.value)}
                      placeholder="Option"
                    />
                    <span className="cpf-price-adj-label">PRICE ADJUSTMENT</span>
                    <div className="cpf-price-adj-input-wrap">
                      <span>$</span>
                      <input
                        className="cpf-price-adj-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={opt.priceAdj}
                        onChange={e => updateOption(attr.id, opt.id, 'priceAdj', e.target.value)}
                      />
                    </div>
                    <button type="button" className="cpf-delete-opt-btn" onClick={() => removeOption(attr.id, opt.id)}>
                      <FaRegTrashAlt />
                    </button>
                  </div>
                ))}
                <div className="cpf-add-option-row">
                  <button type="button" className="cpf-add-option-btn" onClick={() => addOption(attr.id)}>+ Add More Option</button>
                </div>
              </div>
            </div>
          ))}

          {/* Status + Printer */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Status</label>
              <select className="pf-select" name="status" value={form.status} onChange={handleChange}>
                <option>Published</option>
                <option>Unpublished</option>
              </select>
            </div>
            <div className="pf-field">
              <label>Printer <span className="pf-required">*</span></label>
              <select
                className={`pf-select${formErrors.printerReference ? ' pf-input-error' : ''}`}
                name="printerReference"
                value={form.printerReference || ''}
                onChange={handleFormChangePrint}
              >
                <option value="">-- Select Printer --</option>
                {form.printerName && !prinList.some(pr => pr.printerName === form.printerName) && (
                  <option value={form.printerReference}>{form.printerName}</option>
                )}
                {prinList.map((printer) => (
                  <option key={printer.id} value={printer.reference}>
                    {printer.printerName}
                  </option>
                ))}
              </select>
              {formErrors.printerReference && <span className="pf-field-error">{formErrors.printerReference}</span>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pf-actions">
          <button className="pf-cancel-btn" onClick={handleCancel} disabled={isSubmitting}>Cancel</button>
          <button className="pf-save-btn" onClick={handleUpdate} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </main>
    </div>
  );
}

export default ConfigurableProductEditForm;
